import { useState, useEffect, useCallback } from 'react';
import { getStoreAccountStatement, getStoreAccountStatementPdf, getStoreTransactionData } from '../../../Api';
import { STORAGE_CONSTANTS } from '../../../shared/Constants/Constants';
import { toast } from 'react-toastify';

interface TransactionData {
  status: boolean;
  error: string | null;
  message: string;
  order_list: any[];
  total_amount: number;
  total_amount_calculated: number;
  vat_rate: number;
  vat_amount: number;
  total_commission_amount: number;
}

export const useAccountStatement = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [transactionDataLoader, setTransactionDataLoader] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accountStatementData, setAccountStatementData] = useState<any>(null);
  const [transactionData, setTransactionData] = useState<TransactionData | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);

  // Initialize store ID
  useEffect(() => {
    const storedId = localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);
    if (storedId) {
      setStoreId(Number(storedId));
    }
  }, []);

  const fetchAccountStatement = useCallback(async (
    fromDate: string, 
    toDate: string, 
    generateStatement = true
  ) => {
    const storeIdFromStorage = localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);
    setLoading(true);
    setError(null);

    try {
      const data = await getStoreAccountStatement({
        store_id: storeIdFromStorage,
        from_date: fromDate,
        till_date: toDate,
        generate_statement: generateStatement
      });
      
      setAccountStatementData(data);
      return data;
    } catch (error) {
      setError('Failed to fetch account statement');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactionData = useCallback(async (
    fromDate: string,
    tillDate: string,
    searchValue: string
  ) => {
    if (!storeId) {
      console.error("Store ID is null. Please set a valid store ID.");
      return;
    }

    setTransactionData(null);
    setTransactionDataLoader(true);
    setError(null);

    try {
      const data = await getStoreTransactionData(storeId, fromDate, tillDate, searchValue);
      setTransactionData(data);
      return data;
    } catch (error: any) {
      setError('Failed to fetch transaction data.');
      
      if (error?.status !== 404) {
        toast.error(error?.response?.data?.message ?? "Something went wrong");
      }
      throw error;
    } finally {
      setTransactionDataLoader(false);
    }
  }, [storeId]);

  const downloadPDF = useCallback(async (fromDate: string, toDate: string) => {
    if (!storeId) {
      toast.error('Store ID not found');
      return;
    }

    const payload = {
      store_id: storeId,
      from_date: fromDate,
      till_date: toDate,
      pdf_download: true
    };

    try {
      setPdfLoading(true);
      const pdfUrl = await getStoreAccountStatementPdf(payload);
      
      if (pdfUrl) {
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error('Failed to download PDF');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'account_statement.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [storeId]);

  return {
    loading,
    pdfLoading,
    transactionDataLoader,
    error,
    accountStatementData,
    transactionData,
    storeId,
    fetchAccountStatement,
    fetchTransactionData,
    downloadPDF,
  };
};